import React, { useState, useEffect } from 'react';
import { 
  FileText, Upload, RefreshCw, Download, Copy, CheckCircle, AlertCircle,
  Trash2, Eye, File, Shield, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CarrierDocumentsTab = ({ claimId, userId, claim }) => {
  const [documents, setDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGeneratingRebuttal, setIsGeneratingRebuttal] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [rebuttal, setRebuttal] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadDocuments();
    loadRebuttal();
  }, [claimId]);

  const loadDocuments = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/claims/${claimId}/carrier-documents`, {
        params: { user_id: userId }
      });
      setDocuments(res.data.documents || []);
      if (res.data.analysis) setAnalysis(res.data.analysis);
    } catch { /* no docs yet */ }
  };

  const loadRebuttal = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/claims/${claimId}/rebuttal-report`, {
        params: { user_id: userId }
      });
      if (res.data.success) setRebuttal(res.data);
    } catch { /* none yet */ }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    
    setIsUploading(true);
    let uploaded = 0;
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        await axios.post(
          `${BACKEND_URL}/api/claims/${claimId}/carrier-documents?user_id=${userId}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        uploaded++;
      } catch (err) {
        toast.error(`Failed to upload ${file.name}: ${err.response?.data?.detail || 'Unknown error'}`);
      }
    }
    
    if (uploaded > 0) {
      toast.success(`Uploaded ${uploaded} document(s)`);
      loadDocuments();
    }
    setIsUploading(false);
    e.target.value = '';
  };

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/claims/${claimId}/carrier-documents/${docId}`, {
        params: { user_id: userId }
      });
      toast.success('Document removed');
      loadDocuments();
    } catch {
      toast.error('Failed to remove document');
    }
  };

  const handleAnalyze = async () => {
    if (documents.length === 0) {
      toast.error('Upload carrier documents first');
      return;
    }
    setIsAnalyzing(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/claims/${claimId}/analyze-carrier-documents`,
        null,
        { params: { user_id: userId } }
      );
      setAnalysis(res.data.analysis);
      toast.success('Carrier documents analyzed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleGenerateRebuttal = async () => {
    setIsGeneratingRebuttal(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/claims/${claimId}/generate-rebuttal`,
        null,
        { params: { user_id: userId } }
      );
      if (res.data.success) {
        setRebuttal(res.data);
        toast.success('Rebuttal report generated');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate rebuttal');
    } finally {
      setIsGeneratingRebuttal(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = (text, filename) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getDocIcon = (doc) => {
    const name = (doc.file_name || '').toLowerCase();
    if (name.endsWith('.pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (name.match(/\.(jpg|jpeg|png|webp)$/)) return <Eye className="w-5 h-5 text-blue-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  return (
    <div className="space-y-6" data-testid="carrier-documents-tab">
      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600" />
          Insurance Carrier Documents
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Upload denial letters, adjuster estimates, scope of loss reports, or any correspondence from the insurance company. 
          These will be analyzed against your policy and claim data to build your dispute case.
        </p>
        
        <label className="cursor-pointer inline-block">
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.txt,video/*"
            multiple
            onChange={handleUpload}
            className="hidden"
            data-testid="carrier-doc-upload"
          />
          <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 cursor-pointer">
            <Upload className="w-4 h-4" />
            {isUploading ? 'Uploading...' : 'Upload Carrier Documents'}
          </span>
        </label>
        <p className="text-xs text-gray-400 mt-2">PDF, images, DOC, XLS, TXT accepted. Multiple files supported.</p>
      </div>

      {/* Documents List */}
      {documents.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold text-gray-800 mb-3">
            Uploaded Carrier Documents ({documents.length})
          </h3>
          <div className="space-y-2">
            {documents.map(doc => (
              <div key={doc.id} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100">
                {getDocIcon(doc)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{doc.file_name}</p>
                  <p className="text-xs text-gray-500">
                    {doc.doc_type && <span className="mr-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px]">{doc.doc_type}</span>}
                    {doc.uploaded_at && new Date(doc.uploaded_at).toLocaleDateString()}
                    {doc.text_extracted && <span className="ml-2 text-green-600">Text extracted</span>}
                  </p>
                </div>
                {doc.url && (
                  <a href={doc.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800 p-1">
                    <Eye className="w-4 h-4" />
                  </a>
                )}
                <button onClick={() => handleDelete(doc.id)} className="text-red-400 hover:text-red-600 p-1" data-testid={`delete-carrier-doc-${doc.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          
          {/* Analyze Button */}
          <div className="mt-4 flex gap-3">
            <Button
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="analyze-carrier-docs-btn"
            >
              {isAnalyzing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
              ) : (
                <><FileText className="w-4 h-4 mr-2" /> Analyze Against Policy & Claim</>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Analysis Results */}
      {analysis && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Carrier Document Analysis
          </h3>
          <div className="bg-gray-50 rounded-lg p-5 whitespace-pre-wrap text-sm leading-relaxed border" data-testid="carrier-analysis-content">
            {analysis}
          </div>
          <div className="mt-3 flex gap-2">
            <Button variant="outline" size="sm" onClick={() => handleCopy(analysis)}>
              <Copy className="w-4 h-4 mr-1" /> Copy
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleDownload(analysis, `carrier-analysis-${claimId}.txt`)}>
              <Download className="w-4 h-4 mr-1" /> Download
            </Button>
          </div>
        </div>
      )}

      {/* Generate Rebuttal Report */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <Shield className="w-5 h-5 text-red-600" />
          Rebuttal Report
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Generate a comprehensive rebuttal report using all collected evidence — carrier documents, policy analysis, 
          damage photos, field reports, county records, and estimate data. This report is formatted for resubmission to the insurance company.
        </p>

        <Button
          onClick={handleGenerateRebuttal}
          disabled={isGeneratingRebuttal}
          size="lg"
          className="bg-red-600 hover:bg-red-700"
          data-testid="generate-rebuttal-btn"
        >
          {isGeneratingRebuttal ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Rebuttal...</>
          ) : rebuttal?.rebuttal ? (
            <><RefreshCw className="w-4 h-4 mr-2" /> Regenerate Rebuttal Report</>
          ) : (
            <><Shield className="w-4 h-4 mr-2" /> Generate Rebuttal Report</>
          )}
        </Button>

        {rebuttal?.generated_at && (
          <p className="text-xs text-gray-500 mt-2">
            Generated: {new Date(rebuttal.generated_at).toLocaleString()}
          </p>
        )}

        {rebuttal?.rebuttal ? (
          <div className="mt-4">
            <div className="bg-gray-50 rounded-lg p-6 whitespace-pre-wrap text-sm leading-relaxed font-mono border max-h-[600px] overflow-y-auto" data-testid="rebuttal-content">
              {rebuttal.rebuttal}
            </div>
            <div className="mt-3 flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleCopy(rebuttal.rebuttal)} data-testid="copy-rebuttal">
                {copied ? <CheckCircle className="w-4 h-4 mr-1 text-green-600" /> : <Copy className="w-4 h-4 mr-1" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleDownload(rebuttal.rebuttal, `rebuttal-report-${claimId}.txt`)} data-testid="download-rebuttal">
                <Download className="w-4 h-4 mr-1" /> Download
              </Button>
            </div>
          </div>
        ) : (
          !isGeneratingRebuttal && (
            <div className="mt-4 bg-gray-50 rounded-lg p-8 text-center text-gray-500">
              <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No rebuttal report generated yet</p>
              <p className="text-sm mt-1">
                Upload carrier documents and click "Generate Rebuttal Report" to create a comprehensive
                dispute document using all evidence, policy analysis, and county records.
              </p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default CarrierDocumentsTab;
